/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import { useComponentProps } from '@sitecore-content-sdk/nextjs';
// Local
import { withStandardComponentWrapper } from 'helpers/HOC';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import useIsEditing from 'lib/hooks/useIsEditing';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import useDictionary from 'lib/hooks/useDictionary';
import { getTestProps } from 'lib/testing/utils';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const ExpertCardContentBlockClient = (props)=>{
    const { params, fields } = props;
    const { DynamicPlaceholderId, RenderingIdentifier } = params || {};
    const phKey = `expertCard-${DynamicPlaceholderId}`;
    const isEditing = useIsEditing();
    const { getDictionaryValue } = useDictionary();
    // The SDK does not auto-inject `getComponentServerProps` results into the
    // rendered Client Component's props in App Router. Read them back from
    // `ComponentPropsContext` keyed by rendering UID, matching the pattern used
    // by other split components (Breadcrumb, ContextualNav, etc.).
    const ctxProps = useComponentProps(props?.rendering?.uid);
    const staticProps = props?.staticProps ?? ctxProps?.staticProps;
    const styles = parseStyleParams(params, [
        'cta1'
    ]);
    const { container, mainWrapper, contentWrapper, contentHeader, cardSectionMobile, cardSectionDesktop, ctaSection, ctaInnerWrapper, placeholderWrapper, sectionTitleTV, sectionDescriptionTV, sectionCtaTV, divider, cardDesktop } = TAILWIND_VARIANTS();
    const layoutDirection = params?.layoutDirection || 'left';
    const isCardLeft = layoutDirection === 'left';
    const expertiseName = staticProps?.item?.ancestors?.[0]?.areasOfExpertise?.jsonValue?.[0]?.fields?.displayName?.value ?? null;
    const isReady = staticProps !== undefined;
    const linkField = fields?.contentLink;
    const updatedField = !isReady ? linkField : !linkField?.value?.href || !expertiseName ? linkField : (()=>{
        const { href, querystring, linktype } = linkField.value;
        const isInternal = linktype === 'internal';
        const hasQuery = isInternal ? !!querystring : href.includes('?');
        if (hasQuery) return linkField;
        return {
            ...linkField,
            value: {
                ...linkField.value,
                ...(isInternal ? {
                    querystring: `expertise=${encodeURIComponent(expertiseName)}`
                } : {
                    href: `${href}?expertise=${encodeURIComponent(expertiseName)}`
                })
            }
        };
    })();
    if (isEditing) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(EditingHelpText, {
                    priority: "warning",
                    children: getDictionaryValue('ExpertCardAddCardHelpText') || 'Add an ExpertCard to display an expert profile card.'
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: container(),
                    "data-component": "authorable/shared/lists/expertcardcontentblock",
                    ...editingHydrationProps(isEditing),
                    id: RenderingIdentifier,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: mainWrapper({
                            isCardLeft
                        }),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: contentWrapper(),
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: contentHeader(),
                                        children: [
                                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                className: sectionTitleTV(),
                                                field: fields?.title,
                                                tag: "h2",
                                                ...getTestProps(`section-title`)
                                            }),
                                            /*#__PURE__*/ _jsx(RichTextWrapper, {
                                                className: sectionDescriptionTV(),
                                                field: fields?.description,
                                                ...getTestProps(`section-description`)
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: divider()
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: cardSectionMobile(),
                                        children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                                            name: phKey,
                                            rendering: props.rendering,
                                            render: (components)=>/*#__PURE__*/ _jsx("div", {
                                                    className: placeholderWrapper(),
                                                    children: components.map((comp, index)=>/*#__PURE__*/ _jsx("div", {
                                                            children: comp
                                                        }, index))
                                                }),
                                            ...getTestProps(`expert-card-ph`)
                                        })
                                    }),
                                    (isEditing || fields?.contentLink?.value?.href) && /*#__PURE__*/ _jsx("div", {
                                        className: ctaSection(),
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: ctaInnerWrapper(),
                                            children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                                ctaComponentClass: "component-card-item-button-1-color",
                                                className: sectionCtaTV(),
                                                field: updatedField,
                                                ctaIcon: "arrow-right",
                                                ctaIconSize: "s",
                                                ctaStyle: getCtaStyle(styles.cta1, 'secondary'),
                                                ...getTestProps(`section-cta`)
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: cardSectionDesktop(),
                                children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                                    name: phKey,
                                    rendering: props.rendering,
                                    render: (components)=>/*#__PURE__*/ _jsx("div", {
                                            className: placeholderWrapper(),
                                            children: components.map((comp, index)=>/*#__PURE__*/ _jsx("div", {
                                                    children: comp
                                                }, index))
                                        }),
                                    ...getTestProps(`expert-card-ph`)
                                })
                            })
                        ]
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: container(),
        "data-component": "authorable/shared/lists/expertcardcontentblock",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-expert-card-content-block-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsxs("div", {
            className: mainWrapper({
                isCardLeft
            }),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: contentWrapper(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: contentHeader(),
                            children: [
                                /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    className: sectionTitleTV(),
                                    field: fields?.title,
                                    tag: "h2",
                                    ...getTestProps(`section-title`)
                                }),
                                /*#__PURE__*/ _jsx(RichTextWrapper, {
                                    className: sectionDescriptionTV(),
                                    field: fields?.description,
                                    ...getTestProps(`section-description`)
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: divider()
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: cardSectionMobile(),
                            children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                                name: phKey,
                                rendering: props.rendering,
                                render: (components)=>/*#__PURE__*/ _jsx("div", {
                                        className: placeholderWrapper(),
                                        children: components.map((comp, index)=>/*#__PURE__*/ _jsx("div", {
                                                children: comp
                                            }, index))
                                    }),
                                ...getTestProps(`expert-card-ph`)
                            })
                        }),
                        (isEditing || fields?.contentLink?.value?.href) && /*#__PURE__*/ _jsx("div", {
                            className: ctaSection(),
                            children: /*#__PURE__*/ _jsx("div", {
                                className: ctaInnerWrapper(),
                                children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "component-card-item-button-1-color",
                                    className: sectionCtaTV(),
                                    field: updatedField,
                                    ctaIcon: "arrow-right",
                                    ctaIconSize: "s",
                                    ctaStyle: getCtaStyle(styles.cta1, 'secondary'),
                                    ...getTestProps(`section-cta`)
                                })
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: cardSectionDesktop(),
                    children: /*#__PURE__*/ _jsx(PlaceholderWrapper, {
                        name: phKey,
                        rendering: props.rendering,
                        render: (components)=>/*#__PURE__*/ _jsx("div", {
                                className: placeholderWrapper(),
                                children: components.map((comp, index)=>/*#__PURE__*/ _jsx("div", {
                                        className: cardDesktop(),
                                        children: comp
                                    }, index))
                            }),
                        ...getTestProps(`expert-card-ph`)
                    })
                })
            ]
        })
    });
};
export default withStandardComponentWrapper(ExpertCardContentBlockClient, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        container: [
            'w-full'
        ],
        mainWrapper: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-40',
            'md:justify-between'
        ],
        contentWrapper: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'md:gap-spacing-spacing-40',
            'md:w-[37.2%]',
            'lg:w-2/3',
            'md:justify-center'
        ],
        contentHeader: [
            'order-1',
            'md:order-none',
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'md:gap-spacing-spacing-32',
            'items-center',
            'md:items-start'
        ],
        cardSectionMobile: [
            'flex',
            'flex-col',
            'md:w-1/3',
            'order-2',
            'md:hidden'
        ],
        cardSectionDesktop: [
            'flex',
            'flex-col',
            'md:w-[56.92%]',
            'lg:w-1/3',
            'hidden',
            'md:block'
        ],
        ctaSection: [
            'order-3',
            'md:order-none',
            'flex',
            'flex-col',
            'gap-spacing-spacing-32',
            'items-center',
            'md:items-start'
        ],
        ctaInnerWrapper: [
            'flex',
            'flex-wrap',
            'flex-col',
            'gap-spacing-spacing-8',
            'items-center',
            'md:items-start',
            'justify-center',
            'w-full'
        ],
        placeholderWrapper: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-16',
            'md:h-full'
        ],
        sectionTitleTV: [
            'text-component-section-title',
            'font-typography-font-family-header',
            'text-typography-font-size-xlarge',
            'font-normal',
            'leading-typography-line-height-xlarge',
            'text-center',
            'md:text-left'
        ],
        sectionDescriptionTV: [
            'text-component-Accreditations-Reviews-body',
            'font-typography-font-family-body',
            'text-typography-font-size-body-large',
            'font-normal',
            'leading-typography-line-height-body-large',
            'text-center',
            'md:text-left',
            'w-full'
        ],
        sectionCtaTV: [
            'flex',
            'items-center',
            'gap-spacing-spacing-8',
            'py-spacing-spacing-12',
            'px-spacing-spacing-24',
            'border',
            'border-color-border-border',
            'rounded-general-border-radius-button',
            'bg-white',
            'text-color-text-text',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'font-normal',
            'leading-typography-line-height-body-medium',
            'hover:bg-color-grayscale-50',
            'transition-colors',
            'duration-200',
            'w-fit'
        ],
        divider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider'
        ],
        cardDesktop: [
            '[&>article]:flex-col',
            '[&>article>div]:w-full',
            '[&>article>div:first-child]:rounded-bl-none',
            '[&>article>div:last-child]:rounded-tr-none',
            '[&>article>div:first-child>div]:rounded-bl-none',
            'h-full'
        ]
    },
    variants: {
        isCardLeft: {
            true: {
                mainWrapper: [
                    'md:flex-row-reverse',
                    'md:gap-spacing-spacing-40'
                ]
            },
            false: {
                mainWrapper: [
                    'md:flex-row',
                    'md:gap-spacing-spacing-40'
                ]
            }
        }
    }
});
